import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { dequal } from "dequal";

import Tooltip from "@/components/common/Tooltip/Tooltip";
import SideNavSubRoute from "@components/layout/SideNav/SideNavSubRoute";

import type { RouteConfig } from "@routes/routes.types";
import type { RootState } from "@store/store";

import HarrisLogo from "@assets/icons/projectSpecific/HQB_logo.svg?react";
import AngleArrow from "@assets/icons/common/angle_down.svg?react";
import { FiSettings } from "react-icons/fi";
import {
  buildNewURLQueryString,
  getUrlParamsWhileSwitchingPath,
} from "@/utils/url";
import ICONS from "@/constant/IconManager";
import CONFIG from "@/config";
import { ROUTES_CHILDREN_LOOKUP } from "@/routes/routes";
import {
  MARKETS,
  POINT_IN_TIME,
  URL_BY_BRAND,
  URL_BY_MARKET,
  URL_BY_SEGMENT,
} from "@/constant/constantsThrougoutProject";
import Tippy from "@tippyjs/react";
import userService from "@/endpoints/userService";
import SignoutIcon from "@assets/icons/common/signout.svg?react";
import "./SideNav.css";

interface SideNavProps {
  routes: RouteConfig[];
}

const SideNav = ({ routes }: SideNavProps) => {
  const [isAlwaysExpanded, setIsAlwaysExpanded] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [activeRoute, setActiveRoute] = useState<RouteConfig | null>(null);
  const [hoveredRoute, setHoveredRoute] = useState<RouteConfig | null>(null);

  const handleRedirect = (route: RouteConfig) => {
    if (!route.children || route.children.length === 0) {
      return;
    }

    const { children } = route;
    let targetChild: any | undefined;

    const priority = [URL_BY_BRAND, URL_BY_SEGMENT, URL_BY_MARKET];

    const currentPathSegment = priority.find((p) =>
      location.pathname.includes(`/${p}`),
    );

    if (currentPathSegment) {
      targetChild = children.find((child) =>
        child.id.toString().includes(currentPathSegment),
      );
    }

    if (!targetChild) {
      for (const p of priority) {
        targetChild = children.find((child) => child.id.toString().includes(p));
        if (targetChild) break;
      }
    }

    if (!targetChild) {
      targetChild = children[0];
    }

    if (targetChild) {
      const urlQuery = getUrlParamsWhileSwitchingPath(
        "/" + targetChild.id,
        activeGroupDataIds,
        userSelection,
      );
      const newQueryString = buildNewURLQueryString(
        urlQuery,
        urlQuery.mode,
        "/" + targetChild.id,
      );
      navigate(`${targetChild.redirectTo}?${newQueryString}`);
    }
  };

  useLayoutEffect(setActiveRouteFromPath, [location.pathname]);

  function setActiveRouteFromPath() {
    const result = routes.find((route) =>
      location.pathname?.startsWith(`/${route.id}`),
    );
    if (result) {
      setActiveRoute(result);
    } else {
      setActiveRoute(null);
    }
  }

  const isAdmin = useSelector(
    (state: RootState) => state.userDetail?.isAdmin,
    (prev, next) => dequal(prev, next)
  );

  const user = useSelector(
    (state: RootState) => state.userDetail || null,
    (prev, next) => {
      return dequal(prev, next);
    },
  );

  const attributeLabels = useSelector(
    (state: RootState) => {
      if (
        ROUTES_CHILDREN_LOOKUP[location.pathname]?.multipleOptionsForKeys?.[
          POINT_IN_TIME
        ]?.includes(MARKETS)
      ) {
        return state.activeGroup.attributeLabels["global"] || {};
      } else {
        if (state.userSelection.market) {
          const currentMarket = state.userSelection.market;
          if (state.activeGroup.attributeLabels[currentMarket]) {
            return state.activeGroup.attributeLabels[currentMarket];
          }
          return state.activeGroup.attributeLabels["default"] || {};
        }
      }
      return state.activeGroup.attributeLabels["default"] || {};
    },
    (prev, next) => dequal(prev, next),
  );

  const { userSelection, activeGroupDataIds } = useSelector(
    (state: RootState) => ({
      userSelection: state.userSelection,
      activeGroupDataIds: {
          brands: state.activeGroup.brands.ids,
          markets: state.activeGroup.markets.markets.ids?.filter(
          (marketId: number) =>
            state.userSelection.brand
              ? state.activeGroup.brands.entities[
                  state.userSelection.brand
                ].marketIds?.includes(marketId)
              : false,
        ),
        segments: state.activeGroup.segments.segments.ids,
        activeSegments: state.activeGroup.segments.segments.ids?.filter(
          (segmentId: number) => {
            if (
              state.activeGroup.segments.segments.entities[segmentId]?.isActive
            ) {
              return true;
            }
            return false;
          },
        ),
        filters: state.activeGroup.filters.filters.ids?.reduce(
          (acc: number[], curr: number) => {
            const currentFilter =
              state.activeGroup.filters.filters?.entities[curr];
            const filterOptionsIds = currentFilter?.filterOptions?.map(
              (item) => item.filterOptionId,
            );
            return [...acc, ...filterOptionsIds];
          },
          [],
        ),
      },
    }),
    dequal,
  );

  return (
    <div
      className={classNames("flex h-screen shadow-xs text-text-secondary")}
      onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
        const relatedTarget = e.relatedTarget;
        if (
          relatedTarget instanceof Element &&
          (relatedTarget.closest('[data-id="sidenav-submenu"]') ||
            relatedTarget.closest(".tippy-box") ||
            relatedTarget.closest("[data-tippy-root]"))
        ) {
          return;
        }
        setHoveredRoute(null);
        setActiveRouteFromPath();
      }}
    >
      <div className="relative h-screen w-[71px] bg-secondary-500">
        <div className="flex justify-center align-center px-2 pt-[6px] pb-[10px] m-1 mt-3 my-2">
          <HarrisLogo height={32} width={32} />
        </div>
        <div className="flex flex-col items-center justify-between h-[calc(100%-67px)]">
          <ul className="flex flex-col items-center pt-3 justify-center gap-[10px]">
            {routes
              ?.filter((route) => !route.hideNavIcon)
              .map((route) => (
                <Tooltip
                  key={route.id}
                  contentList={[
                    attributeLabels?.[`${route?.key}`]?.label ?? route?.name,
                  ]}
                  offset={isAlwaysExpanded ? [0, 5] : [0, 20]}
                  placement="right"
                >
                  <li
                    className={classNames(
                      "p-[10px] rounded-lg border-1  cursor-pointer",
                      location.pathname?.startsWith(`/${route.id}`)
                        ? "bg-ternary-500"
                        : "",
                      !location.pathname?.startsWith(`/${route.id}`) &&
                        hoveredRoute &&
                        hoveredRoute.id === route.id
                        ? "border-[#1b404d]"
                        : "border-transparent",
                    )}
                    onMouseEnter={() => {
                      setHoveredRoute(route);
                    }}
                    onClick={() => {
                      handleRedirect(route);
                    }}
                  >
                    {route.iconKey ? (
                      <img
                        src={ICONS[route.iconKey]?.light}
                        alt={route.name}
                        className="h-[22px] w-[22px]"
                      />
                    ) : (
                      ""
                    )}
                    {location.pathname?.startsWith(
                      `/${route.id}` ? "bg-ternary-500" : "",
                    )}
                  </li>
                </Tooltip>
              ))}
          </ul>
          <div className="flex flex-col items-center">
            {/* Conditionally display admin settings icon based on user role */}
            {isAdmin && <Tooltip contentList={["Admin"]} offset={[0, 5]} placement="right">
              <div
                className="p-[10px] rounded-lg border-1 border-transparent cursor-pointer hover:bg-ternary-500 transition-colors mb-[10px]"
                onClick={() => {
                  window.location.href = CONFIG.endPoints.legacyUi;
                }}
              >
                <FiSettings className="h-[20px] w-[20px] text-white" />
              </div>
            </Tooltip>}
            <div className="border-t-2 border-[#1b404d54] px-1 py-[19px]">
              {isAlwaysExpanded ? (
                <Tooltip
                  contentList={user?.name ? [user?.name] : ["Loading..."]}
                  offset={[0, 5]}
                >
                  <div
                    className={classNames(
                      "w-[40px] h-[40px] rounded-[50%] flex items-center justify-center",
                      user?.name
                        ? "bg-[#F2F4F7] text-[#667085] font-semibold text-large"
                        : "bg-[#F2F4F7] opacity-50",
                    )}
                  >
                    {user?.name ? user?.name.slice(0, 1)?.toUpperCase() : ""}
                  </div>
                </Tooltip>
              ) : (
                <Tippy
                  interactive={true}
                  trigger="click"
                  zIndex={900}
                  className={`user-name-tooltip-container`}
                  arrow={false}
                  offset={[5, 18]}
                  placement={"right-end"}
                  content={[
                    <div>
                      <div className="w-full">
                        <div className="border-t-[2px] pr-0 py-[6px] pl-0 border-[#1b404d54] mx-2 text-base flex items-center justify-between">
                          <div className="w-[calc(100%-22px)] h-[40px]">
                            {user?.name ? (
                              <div className="font-medium">{user?.name}</div>
                            ) : (
                              ""
                            )}
                            {user?.email && (
                              <Tooltip
                                contentList={[
                                  user?.email
                                    ? user?.email
                                    : "Something went wrong",
                                ]}
                                offset={[0, 5]}
                                placement="top"
                              >
                                <div className="text-xs font-light text-ellipsis overflow-hidden opacity-70 w-full">
                                  {user?.email}
                                </div>
                              </Tooltip>
                            )}
                          </div>
                          <Tooltip
                            contentList={["Sign out"]}
                            offset={[0, 5]}
                            placement="right"
                          >
                            <div
                              className="p-1 cursor-pointer"
                              onClick={userService.auth.logout}
                            >
                              <SignoutIcon height={22} />
                            </div>
                          </Tooltip>
                        </div>
                      </div>
                    </div>,
                  ]}
                >
                  <div
                    className={classNames(
                      "w-[40px] h-[40px] rounded-[50%] cursor-pointer flex items-center justify-center",
                      user?.name
                        ? "bg-[#F2F4F7] text-[#667085] font-semibold text-large"
                        : "bg-[#F2F4F7] opacity-50",
                    )}
                  >
                    {user?.name ? user?.name.slice(0, 1)?.toUpperCase() : ""}
                  </div>
                </Tippy>
              )}
            </div>
          </div>
        </div>
        <Tooltip
          contentList={[isAlwaysExpanded ? "Unpin" : "Pin"]}
          offset={[5, 5]}
          placement="top"
        >
          <div
            onClick={() => {
              setIsAlwaysExpanded((prev) => !prev);
            }}
            className="rounded-[50%] absolute z-[101] bottom-[92px] right-[-12px] bg-secondary-500 border w-fit leading-none cursor-pointer p-1  border-[#1b404d]"
          >
            <AngleArrow
              height={16}
              width={16}
              className={classNames(
                "transition",
                isAlwaysExpanded ? "rotate-[90deg]" : "rotate-[-90deg]",
              )}
            />
          </div>
        </Tooltip>
      </div>

      {isAlwaysExpanded && (
        <div
          data-id="sidenav-submenu"
          className={classNames(
            "bg-secondary-500 transition relative w-[221px] border-l  border-[#1b404d] h-full",
          )}
        >
          <SideNavSubRoute
            activeRoute={hoveredRoute || activeRoute}
            setIsExpanded={() => { }}
          />
        </div>
      )}
    </div>
  );
};

export default SideNav;
